import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

const castStringToBool = z.preprocess((val) => {
  if (typeof val === "string") {
    if (["1", "true"].includes(val.toLowerCase())) return true;
    if (["0", "false"].includes(val.toLowerCase())) return false;
  }
  return val;
}, z.coerce.boolean());

const APP_URL_T = z.string().url().default("https://app.optogrid.com");
export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app isn't
   * built with invalid env vars.
   */
  server: {
    DATABASE_URL: z.string().url(),
    S3_AWS_ACCESS_KEY_ID: z.string(),
    S3_AWS_SECRET_ACCESS_KEY: z.string(),
    S3_BUCKET_NAME: z.string(),
    AWS_REGION: z.string().default("us-east-1"),
    // COGNITO_USER_POOL_ID: z.string(),
    // COGNITO_WEB_CLIENT_ID: z.string(),

    APP_URL: APP_URL_T,

    MEASUREMENT_ID: z.string().optional(),

    STRIPE_SECRET_KEY: z.string(),
    STRIPE_ENDPOINT_SECRET: z.string().optional(),

    // Shared secret for the RevenueCat webhook Authorization header.
    // Optional so builds don't fail, but the webhook route returns 503 until set.
    REVENUECAT_WEBHOOK_SECRET: z.string().optional(),

    SENDGRID_API_KEY: z.string(),
    SENDGRID_PARTNER_INVITE_TEMPLATE_ID: z
      .string()
      .default("d-fa7d34fda496458c9dadffa5363e505a"),

    FREE_TIER_LIMITS: z.coerce.number().default(3),
    ENFORCE_PLAN_LIMITS: z.enum(["true", "false"]).default("false"),
    FREE_TIER_MEASUREMENTS_LIMIT: z.coerce.number().optional(),

    CRON_SECRET: z.string().min(16).optional(),

    /**
     * E2E only: comma-separated feature-flag keys to force ON, bypassing the
     * PostHog round-trip. Honored only in e2e builds (NEXT_PUBLIC_E2E=1) and
     * scoped to the listed keys, so it can never enable a flag in prod.
     */
    E2E_FORCE_FLAGS: z.string().optional(),
  },
  /**
   * Specify your client-side environment variables schema here.
   * For them to be exposed to the client, prefix them with `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_MEASUREMENT_ID: z.string().optional(),
    NEXT_PUBLIC_GTM_ID: z.string().optional(),

    NEXT_PUBLIC_STRIPE_PRICING_TABLE_ID: z.string(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),

    NEXT_PUBLIC_APP_URL: APP_URL_T,

    NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().default("https://us.i.posthog.com"),
    /** Route browser PostHog traffic through our own domain (see lib/posthog-proxy.mjs) */
    NEXT_PUBLIC_POSTHOG_PROXY: castStringToBool.default(false),
    // NEXT_PUBLIC_ENABLE_PARTNER_LINK: castStringToBool.default(false),
  },
  shared: {
    ENABLE_PARTNER_LINK: castStringToBool.default(false),
  },
  /**
   * Destructure all variables from `process.env` to make sure they aren't tree-shaken away.
   */
  runtimeEnv: {
    DATABASE_URL: process.env.DATABASE_URL,
    S3_AWS_ACCESS_KEY_ID: process.env.S3_AWS_ACCESS_KEY_ID,
    S3_AWS_SECRET_ACCESS_KEY: process.env.S3_AWS_SECRET_ACCESS_KEY,
    S3_BUCKET_NAME: process.env.S3_BUCKET_NAME,
    // NEXT_PUBLIC_CLIENTVAR: process.env.NEXT_PUBLIC_CLIENTVAR,

    NEXT_PUBLIC_MEASUREMENT_ID: process.env.NEXT_PUBLIC_MEASUREMENT_ID,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,

    APP_URL: process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL,

    MEASUREMENT_ID: process.env.NEXT_PUBLIC_MEASUREMENT_ID,
    //

    AWS_REGION: process.env.AWS_REGION,

    //
    // COGNITO_USER_POOL_ID: process.env.COGNITO_USER_POOL_ID,
    // COGNITO_WEB_CLIENT_ID: process.env.COGNITO_WEB_CLIENT_ID,

    NEXT_PUBLIC_STRIPE_PRICING_TABLE_ID:
      process.env.NEXT_PUBLIC_STRIPE_PRICING_TABLE_ID,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,

    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_POSTHOG_PROXY: process.env.NEXT_PUBLIC_POSTHOG_PROXY,

    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_ENDPOINT_SECRET: process.env.STRIPE_ENDPOINT_SECRET,
    REVENUECAT_WEBHOOK_SECRET: process.env.REVENUECAT_WEBHOOK_SECRET,

    SENDGRID_API_KEY: process.env.SENDGRID_API_KEY,
    SENDGRID_PARTNER_INVITE_TEMPLATE_ID:
      process.env.SENDGRID_PARTNER_INVITE_TEMPLATE_ID,

    ENABLE_PARTNER_LINK: process.env.NEXT_PUBLIC_ENABLE_PARTNER_LINK,

    FREE_TIER_LIMITS: process.env.FREE_TIER_LIMITS,
    ENFORCE_PLAN_LIMITS: process.env.ENFORCE_PLAN_LIMITS,
    FREE_TIER_MEASUREMENTS_LIMIT: process.env.FREE_TIER_MEASUREMENTS_LIMIT,
    CRON_SECRET: process.env.CRON_SECRET,
    E2E_FORCE_FLAGS: process.env.E2E_FORCE_FLAGS,
  },

  skipValidation: !!process.env.CI || !!process.env.SKIP_ENV_VALIDATION,
});
